/**
 * Tabs component that builds accessible markup from nested blocks.
 * CSS classes from source tab/tabpanel elements are preserved.
 *
 * @example
 * <!-- Input from blocks -->
 * <tabs-component>
 *   <div data-tab-group="block-id-1">
 *     <h4 data-tab-role="tab" class="tab-heading">Tab 1</h4>
 *     <div data-tab-role="tabpanel" class="tab-content">Content 1</div>
 *   </div>
 * </tabs-component>
 *
 * <!-- Generated output -->
 * <tabs-component>
 *   <div role="tablist">
 *     <button role="tab" class="tab-heading" id="Tab-block-id-1" ...>Tab 1</button>
 *   </div>
 *   <div role="tabpanel" class="tab-content" id="Panel-block-id-1" ...>
 *     <div>Content 1</div>
 *   </div>
 * </tabs-component>
 */
class TabsComponent extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  /** @type {HTMLElement | null} */
  #tablist = null;

  /** @type {HTMLElement[]} */
  get tabs() {
    return Array.from(this.querySelectorAll('[role="tab"]'));
  }

  /** @type {HTMLElement[]} */
  get panels() {
    return Array.from(this.querySelectorAll('[role="tabpanel"]'));
  }

  connectedCallback() {
    this.#buildMarkup();

    if (!this.#tablist) return;

    this.#abort?.abort();
    this.#abort = new AbortController();
    const opts = { signal: this.#abort.signal };

    this.#tablist.addEventListener('keydown', this.#handleKeyDown, opts);
    this.#tablist.addEventListener('click', this.#handleClick, opts);
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  #buildMarkup() {
    const groups = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-tab-group]')));
    if (!groups.length) return;

    const tablist = document.createElement('div');
    tablist.setAttribute('role', 'tablist');
    // Insert the tablist as the first child so it precedes every panel.
    this.insertBefore(tablist, this.firstChild);

    /** @type {Set<string>} */
    const usedIds = new Set();

    groups.forEach((group, index) => {
      const rawId = group.getAttribute('data-tab-group') || `tab-${index}`;
      const id = usedIds.has(rawId) ? `${rawId}-${index}` : rawId;
      usedIds.add(id);
      const tabSource = /** @type {HTMLElement | null} */ (group.querySelector('[data-tab-role="tab"]'));
      const panelSource = /** @type {HTMLElement | null} */ (group.querySelector('[data-tab-role="tabpanel"]'));

      if (!tabSource || !panelSource) return;

      const isFirst = index === 0;
      const tabId = `Tab-${id}`;
      const panelId = `Panel-${id}`;

      const tab = document.createElement('button');
      tab.setAttribute('type', 'button');
      tab.setAttribute('role', 'tab');
      tab.setAttribute('id', tabId);
      tab.setAttribute('aria-selected', String(isFirst));
      tab.setAttribute('aria-controls', panelId);
      tab.setAttribute('tabindex', isFirst ? '0' : '-1');
      if (tabSource.className) tab.className = tabSource.className;

      for (const attr of tabSource.attributes) {
        if (attr.name.startsWith('data-') && attr.name !== 'data-tab-role') {
          tab.setAttribute(attr.name, attr.value);
        }
      }

      tab.innerHTML = tabSource.innerHTML;
      tablist.appendChild(tab);
      tabSource.remove();

      // Reuse the group wrapper itself as the tabpanel. The panel content (and
      // any custom elements inside it) is never relocated, so it
      // stays continuously connected and its connectedCallback runs only once.
      group.setAttribute('role', 'tabpanel');
      group.setAttribute('id', panelId);
      group.setAttribute('aria-labelledby', tabId);
      group.hidden = !isFirst;
      group.removeAttribute('data-tab-group');

      if (panelSource.className) {
        group.className = panelSource.className;
        panelSource.removeAttribute('class');
      }
      panelSource.removeAttribute('data-tab-role');
    });

    this.#tablist = tablist;
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const tab = /** @type {HTMLElement | null} */ (event.target)?.closest('[role="tab"]');
    if (tab instanceof HTMLElement) {
      event.preventDefault();
      this.#selectTab(tab);
    }
  };

  /** @param {KeyboardEvent} event */
  #handleKeyDown = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement) || target.getAttribute('role') !== 'tab') return;

    const tabs = this.tabs;
    const currentIndex = tabs.indexOf(target);

    /** @type {Record<string, () => void>} */
    const actions = {
      ArrowLeft: () => this.#focusTab(tabs, currentIndex - 1),
      ArrowRight: () => this.#focusTab(tabs, currentIndex + 1),
      Home: () => this.#focusTab(tabs, 0),
      End: () => this.#focusTab(tabs, tabs.length - 1),
      Enter: () => this.#selectTab(target),
      ' ': () => this.#selectTab(target),
    };

    const action = actions[event.key];
    if (action) {
      event.preventDefault();
      action();
    }
  };

  /**
   * @param {HTMLElement[]} tabs
   * @param {number} index
   */
  #focusTab(tabs, index) {
    const targetIndex = (index + tabs.length) % tabs.length;
    const target = tabs[targetIndex];
    if (!target) return;

    tabs.forEach((tab, tabIndex) => {
      tab.setAttribute('tabindex', tabIndex === targetIndex ? '0' : '-1');
    });

    target.focus();
  }

  /** @param {HTMLElement} tab */
  #selectTab(tab) {
    if (tab.getAttribute('aria-selected') === 'true') return;

    const panelId = tab.getAttribute('aria-controls');
    const panel = panelId ? this.querySelector(`#${CSS.escape(panelId)}`) : null;
    if (!panel) return;

    for (const t of this.tabs) {
      const isSelected = t === tab;
      t.setAttribute('aria-selected', String(isSelected));
      t.setAttribute('tabindex', isSelected ? '0' : '-1');
    }

    for (const p of this.panels) {
      p.hidden = p !== panel;
    }

    this.dispatchEvent(new TabChangeEvent(tab, panel));
  }
}

export class TabChangeEvent extends Event {
  /**
   * @param {HTMLElement} tab
   * @param {Element} panel
   */
  constructor(tab, panel) {
    super(TabChangeEvent.eventName, { bubbles: true });
    this.detail = { tab, panel };
  }

  static eventName = 'tabs:change';
}

if (!customElements.get('tabs-component')) {
  customElements.define('tabs-component', TabsComponent);
}
